import { useEffect, type RefObject } from 'react';

/**
 * Loop a video over one CHAPTER of a longer clip.
 *
 * Each guide is a single journey recording; a section shows only its own slice
 * of it (see scripts/generate-guide-journeys.ts, which writes the time ranges to
 * public/data/guides/chapters/<slug>.json). This seeks to `start` and jumps back
 * there whenever playback passes `end`, so the section loops its own step rather
 * than the whole journey.
 *
 * Native `loop` still handles the case where no chapter is set.
 */
export function useChapterLoop(
  ref: RefObject<HTMLVideoElement | null>,
  start: number | undefined,
  end: number | undefined,
  dep: string,
): void {
  useEffect(() => {
    const v = ref.current;
    if (!v || start === undefined) return;

    const from = Math.max(0, start);
    // A hair inside the boundary: seeking exactly to `end` can land on the first
    // frame of the next chapter on some browsers' keyframe rounding.
    const to = end !== undefined && end > from ? end - 0.05 : undefined;

    const seekToStart = () => {
      // Only rewind when we're outside the chapter — otherwise a `seeked` event
      // fired by our own rewind would fight normal playback.
      if (Math.abs(v.currentTime - from) > 0.25) v.currentTime = from;
    };

    const onTimeUpdate = () => {
      if (to !== undefined && v.currentTime >= to) v.currentTime = from;
      else if (v.currentTime < from - 0.25) v.currentTime = from;
    };

    // Native `loop` restarts at 0 — pull it back into the chapter.
    const onEnded = () => { v.currentTime = from; };

    if (v.readyState >= 1) seekToStart();
    v.addEventListener('loadedmetadata', seekToStart);
    v.addEventListener('timeupdate', onTimeUpdate);
    v.addEventListener('ended', onEnded);

    return () => {
      v.removeEventListener('loadedmetadata', seekToStart);
      v.removeEventListener('timeupdate', onTimeUpdate);
      v.removeEventListener('ended', onEnded);
    };
  }, [ref, start, end, dep]);
}
